// ============================================================================
// Electronic Signature Component
// Captures legally compliant e-signatures with timestamp, IP, and consent
// ============================================================================

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Pen, 
  Type, 
  Trash2, 
  Check, 
  AlertTriangle,
  Shield,
  Clock,
  FileText,
  Fingerprint
} from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

// ============================================================================
// LEGAL CONSENT TEXT
// ============================================================================

const DEFAULT_CONSENT_TEXT = `
By signing below, I acknowledge and agree that:

1. **Electronic Signature Agreement**: I understand that by typing my name, drawing my signature, or clicking "I Agree," I am creating an electronic signature that is legally binding and has the same legal effect as a handwritten signature.

2. **Consent to Electronic Records**: I consent to receive, sign, and store documents electronically. I understand that I may request paper copies at any time.

3. **Accuracy**: I certify that the information I have provided is true, complete, and accurate to the best of my knowledge.

4. **Intent to Sign**: I intend for my electronic signature to apply to this document and all associated materials as indicated.

5. **ESIGN Act Compliance**: This electronic signature complies with the Electronic Signatures in Global and National Commerce Act (ESIGN Act) and applicable state laws.
`;

// ============================================================================
// SIGNATURE PAD COMPONENT (Drawing)
// ============================================================================

function SignaturePad({ onChange, onClear, initialValue }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1e3a5f';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Set canvas size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * 2;
    canvas.height = rect.height * 2;
    ctx.scale(2, 2);

    // Draw initial value if provided
    if (initialValue) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, rect.width, rect.height);
        setHasSignature(true);
      };
      img.src = initialValue;
    }
  }, [initialValue]);

  const startDrawing = useCallback((e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    let x, y;
    if (e.type === 'mousedown') {
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    } else if (e.type === 'touchstart') {
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    }
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasSignature(true);
  }, []);

  const draw = useCallback((e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    let x, y;
    if (e.type === 'mousemove') {
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    } else if (e.type === 'touchmove') {
      e.preventDefault();
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    }
    
    ctx.lineTo(x, y);
    ctx.stroke();
  }, [isDrawing]);

  const stopDrawing = useCallback(() => {
    if (isDrawing) {
      setIsDrawing(false);
      const canvas = canvasRef.current;
      const dataUrl = canvas.toDataURL('image/png');
      onChange?.(dataUrl);
    }
  }, [isDrawing, onChange]);

  const clearSignature = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, rect.height);
    setHasSignature(false);
    onChange?.(null);
    onClear?.();
  }, [onChange, onClear]);

  return (
    <div className="space-y-2">
      <div className="relative">
        <canvas
          ref={canvasRef}
          className="w-full h-32 border-2 border-dashed border-gray-300 rounded-lg bg-white cursor-crosshair touch-none"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        {!hasSignature && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-gray-400">
            <span>Sign here</span>
          </div>
        )}
      </div>
      {hasSignature && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={clearSignature}
          className="text-red-600 hover:text-red-700"
        >
          <Trash2 className="h-4 w-4 mr-1" />
          Clear Signature
        </Button>
      )}
    </div>
  );
}

// ============================================================================
// TYPED SIGNATURE COMPONENT
// ============================================================================

function TypedSignature({ onChange, value, fullName }) {
  const [typedName, setTypedName] = useState(value || '');

  const handleChange = (e) => {
    const name = e.target.value;
    setTypedName(name);
    onChange?.(name);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="typed-signature">Type your full legal name</Label>
        <Input
          id="typed-signature"
          value={typedName}
          onChange={handleChange}
          placeholder="Type your full name as your signature"
          className="text-lg"
        />
      </div>
      
      {typedName && (
        <div className="p-4 bg-gray-50 rounded-lg border">
          <Label className="text-xs text-gray-500 mb-2 block">Signature Preview:</Label>
          <p className="text-2xl font-signature text-gray-800 italic">{typedName}</p>
        </div>
      )}

      {fullName && typedName && typedName.toLowerCase() !== fullName.toLowerCase() && (
        <Alert variant="warning" className="bg-yellow-50 border-yellow-200">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800">
            The typed name does not match your full name on file ({fullName}). 
            Please ensure you type your legal name exactly.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}

// ============================================================================
// MAIN E-SIGNATURE COMPONENT
// ============================================================================

export default function ESignature({
  documentTitle,
  documentType,
  documentId,
  signerId,
  signerName,
  signerEmail,
  consentText = DEFAULT_CONSENT_TEXT,
  onSign,
  onCancel,
  requiredFullNameMatch = false,
  showTimestamp = true,
  disabled = false,
  initialSignature = null,
  className = '',
}) {
  const [signatureType, setSignatureType] = useState('draw'); // 'draw' | 'type'
  const [drawnSignature, setDrawnSignature] = useState(initialSignature?.type === 'drawn' ? initialSignature.data : null);
  const [typedSignature, setTypedSignature] = useState(initialSignature?.type === 'typed' ? initialSignature.data : '');
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [ipAddress, setIpAddress] = useState('');

  // Get client IP address
  useEffect(() => {
    const getIP = async () => {
      try {
        // In production, this would be handled server-side
        // For demo, we'll use a placeholder
        setIpAddress('192.168.1.1'); // Placeholder - would be actual IP in production
      } catch (e) {
        setIpAddress('unknown');
      }
    };
    getIP();
  }, []);

  const hasValidSignature = signatureType === 'draw' 
    ? !!drawnSignature 
    : typedSignature.length >= 2;

  const canSubmit = hasValidSignature && consentChecked && !disabled;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const timestamp = new Date().toISOString();
      const userAgent = navigator.userAgent;

      const signatureData = {
        id: uuidv4(),
        documentId,
        documentType,
        documentTitle,
        signerId,
        signerName,
        signerEmail,
        signatureType: signatureType === 'draw' ? 'drawn' : 'typed',
        signatureData: signatureType === 'draw' ? drawnSignature : typedSignature,
        consentText,
        consentAccepted: true,
        timestamp,
        ipAddress,
        userAgent,
        legalText: `I, ${signerName}, hereby acknowledge that by ${signatureType === 'draw' ? 'drawing my signature' : 'typing my name'} above, I am electronically signing the document "${documentTitle}" on ${new Date(timestamp).toLocaleString()}. This electronic signature is legally binding and equivalent to my handwritten signature.`,
        metadata: {
          browser: navigator.userAgent,
          platform: navigator.platform,
          language: navigator.language,
          screenResolution: `${window.screen.width}x${window.screen.height}`,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      };

      await onSign?.(signatureData);
    } catch (err) {
      setError(err.message || 'Failed to submit signature. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className={`w-full ${className}`}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Fingerprint className="h-5 w-5 text-blue-600" />
              Electronic Signature
            </CardTitle>
            <CardDescription>
              Sign "{documentTitle}" electronically
            </CardDescription>
          </div>
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            <Shield className="h-3 w-3 mr-1" />
            Secure
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Signer Information */}
        <div className="p-4 bg-gray-50 rounded-lg border">
          <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Signer Information
          </h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Name:</span>
              <span className="ml-2 font-medium">{signerName}</span>
            </div>
            <div>
              <span className="text-gray-500">Email:</span>
              <span className="ml-2 font-medium">{signerEmail}</span>
            </div>
          </div>
        </div>

        {/* Signature Method Selection */}
        <Tabs value={signatureType} onValueChange={setSignatureType}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="draw" className="flex items-center gap-2">
              <Pen className="h-4 w-4" />
              Draw Signature
            </TabsTrigger>
            <TabsTrigger value="type" className="flex items-center gap-2">
              <Type className="h-4 w-4" />
              Type Signature
            </TabsTrigger>
          </TabsList>

          <TabsContent value="draw" className="mt-4">
            <SignaturePad
              onChange={setDrawnSignature}
              onClear={() => setDrawnSignature(null)}
              initialValue={drawnSignature}
            />
            <p className="text-xs text-gray-500 mt-2">
              Use your mouse or finger to draw your signature in the box above.
            </p>
          </TabsContent>

          <TabsContent value="type" className="mt-4">
            <TypedSignature
              onChange={setTypedSignature}
              value={typedSignature}
              fullName={requiredFullNameMatch ? signerName : null}
            />
          </TabsContent>
        </Tabs>

        {/* Legal Consent */}
        <div className="space-y-4">
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-100 max-h-48 overflow-y-auto">
            <h4 className="text-sm font-medium text-blue-800 mb-2">Legal Consent</h4>
            <div className="text-xs text-blue-700 whitespace-pre-line prose prose-sm">
              {consentText}
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <Checkbox
              id="consent"
              checked={consentChecked}
              onCheckedChange={setConsentChecked}
              disabled={disabled}
            />
            <div className="grid gap-1.5 leading-none">
              <label
                htmlFor="consent"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                I agree to the terms above and consent to sign electronically
              </label>
              <p className="text-xs text-gray-500">
                By checking this box, you acknowledge that you have read and agree to the legal consent terms.
              </p>
            </div>
          </div>
        </div>

        {/* Timestamp & Security Info */}
        {showTimestamp && (
          <div className="flex items-center justify-between text-xs text-gray-500 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {new Date().toLocaleString()}
              </span>
              <span className="flex items-center gap-1">
                <Shield className="h-3 w-3" />
                IP: {ipAddress || 'Detecting...'}
              </span>
            </div>
            <span className="text-green-600 flex items-center gap-1">
              <Check className="h-3 w-3" />
              256-bit Encryption
            </span>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </CardContent>

      <CardFooter className="flex justify-between border-t pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit || isSubmitting}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {isSubmitting ? (
            <>
              <span className="animate-spin mr-2">⏳</span>
              Signing...
            </>
          ) : (
            <>
              <Check className="h-4 w-4 mr-2" />
              Sign Document
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}

// ============================================================================
// SIGNATURE DISPLAY COMPONENT (for viewing signed documents)
// ============================================================================

export function SignatureDisplay({ signature, showDetails = true }) {
  if (!signature) return null;

  return (
    <div className="p-4 border rounded-lg bg-gray-50">
      <div className="flex items-center justify-between mb-3">
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
          <Check className="h-3 w-3 mr-1" />
          Signed
        </Badge>
        <span className="text-xs text-gray-500">
          {new Date(signature.timestamp).toLocaleString()}
        </span>
      </div>

      {/* Signature */}
      <div className="p-3 bg-white rounded border mb-3">
        {signature.signatureType === 'drawn' ? (
          <img
            src={signature.signatureData}
            alt="Signature"
            className="h-16 w-auto"
          />
        ) : (
          <p className="text-xl font-signature italic text-gray-800">
            {signature.signatureData}
          </p>
        )}
      </div>

      {showDetails && (
        <div className="text-xs text-gray-500 space-y-1">
          <p><strong>Signer:</strong> {signature.signerName} ({signature.signerEmail})</p>
          <p><strong>Signed:</strong> {new Date(signature.timestamp).toLocaleString()}</p>
          <p><strong>Method:</strong> {signature.signatureType === 'drawn' ? 'Drawn Signature' : 'Typed Signature'}</p>
          <p><strong>IP Address:</strong> {signature.ipAddress}</p>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SIGNATURE VERIFICATION BADGE
// ============================================================================

export function SignatureVerificationBadge({ verified, timestamp }) {
  return (
    <div className="inline-flex items-center gap-2">
      {verified ? (
        <Badge className="bg-green-100 text-green-800 border-green-200">
          <Check className="h-3 w-3 mr-1" />
          Verified Signature
        </Badge>
      ) : (
        <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Pending Verification
        </Badge>
      )}
      {timestamp && (
        <span className="text-xs text-gray-500">
          {new Date(timestamp).toLocaleDateString()}
        </span>
      )}
    </div>
  );
}
